<template>
  <!-- Zoomable/fullscreen-option image -->
  <div v-if="isZoom" class="relative">
    <div :id="customId" class="bg-white" :customKey="imageKey">
      <pdl-button
        v-if="isFullscreen"
        tertiary
        icon-only
        icon="close"
        :label="exitFullScreenLabel"
        class="absolute top-4 right-4"
        @click="toggleFullscreen"
      />
    </div>
    <pdl-button
      tertiary
      icon-only
      icon="open_in_full"
      :label="fullScreenLabel"
      class="absolute bottom-4 right-4 z-10"
      @click="toggleFullscreen"
    />
  </div>

  <!-- Base image -->
  <img v-else :id="customId" :src="src" :alt="alt" @load="onImageLoad" />
</template>

<script>
//https://cloudinary.com/documentation/product_gallery_reference
import {PdlButton} from '@pedal/pdl-button';

export default {
  name: 'PdlImage',
  components: {PdlButton},
  props: {
    /**
     * Alternate text for screen readers. Leave empty for decorative images.
     */
    alt: {
      type: String,
      default: null,
    },
    /**
     * Dynamic label for full screen button, WCAG translations-friendly
     */
    fullScreenLabel: {
      type: String,
      default: 'Full Screen',
    },
    /**
     * Dynamic label for closing full screen button, WCAG translations-friendly
     */
    exitFullScreenLabel: {
      type: String,
      default: 'Exit Full Screen',
    },
    /**
     * Custom id for image.
     */
    customId: {
      type: String,
      default: '',
    },
    /**
     * Optional key, pass to ensure that Cloudinary image layers update immediately when zooming
     */
    customKey: {
      type: String,
      default: '',
    },
    /**
     * Image url.
     */
    src: {
      type: String,
      default: '',
    },
    /**
     * Zoom variant utilizing Cloudinary Product Gallery.
     */
    isZoom: {
      type: Boolean,
      default: false,
    },
    /**
     * Percentage of zoom.
     */
    zoomLevel: {
      type: Number,
      default: 2.5,
    },
    /**
     * Layers needed to build url string for zoom variant.
     */
    imageLayers: {
      type: Array,
      default: () => [],
    },
    /**
     * Applies aspect ratio (width:height) to zoom variant.
     */
    aspectRatio: {
      type: String,
      default: undefined,
      validator: (val) =>
        ['square', '1:1', '16:9', '9:16', '3:4', '4:3', '4:6', '6:4', '5:7', '7:5', '8:5', '5:8'].includes(val),
    },
  },

  data() {
    return {
      gallery: {},
      isGalleryLoaded: false,
      isFullscreen: false,
    };
  },

  computed: {
    overlayTransformations() {
      return this.imageLayers.map((layer) => {
        return {overlay: layer};
      });
    },
    imageKey() {
      // Serialize the image layers + src to generate value for key
      // Forces Vue to re-render Cloudinary image gallery under the hood when new selections are made reactively
      return this.customKey?.length ? this.customKey : `${this.src}-${JSON.stringify(this.imageLayers)}`;
    },
  },

  watch: {
    imageLayers() {
      if (this.isZoom && this.isGalleryLoaded) {
        this.gallery.update({
          mediaAssets: [
            {
              publicId: 'p1_base',
              transformation: {
                transformation: this.overlayTransformations,
              },
            },
          ],
        });
      }
    },
  },
  beforeUnmount() {
    if (this.isGalleryLoaded) {
      this.gallery.destroy();
    }
    document.removeEventListener('fullscreenchange', this.trackFullscreenChange);
  },
  mounted() {
    if (this.isZoom && !this.isGalleryLoaded) {
      this.initGallery();
    }
    document.addEventListener('fullscreenchange', this.trackFullscreenChange);
  },
  methods: {
    initGallery() {
      const isScriptLoaded = !!document.getElementById('pedal-product-gallery-script');
      isScriptLoaded ? this.loadGallery() : this.appendGalleryScript();
    },
    appendGalleryScript() {
      const script = document.createElement('script');
      script.src = 'https://product-gallery.cloudinary.com/all.js';
      script.id = 'pedal-product-gallery-script';
      document.head.appendChild(script);
      script.onload = this.loadGallery;
    },
    loadGallery() {
      this.gallery = window.cloudinary.galleryWidget({
        container: `#${this.customId}`,
        cloudName: 'trekbikes',
        private_cdn: true,
        secure: true,
        carouselStyle: 'none',
        loaderProps: {style: 'circle'},
        zoomProps: {
          type: 'inline',
          level: this.zoomLevel,
        },
        aspectRatio: this.aspectRatio,
        mediaAssets: [
          {
            publicId: 'p1_base',
            transformation: {
              transformation: this.overlayTransformations,
            },
          },
        ],
      });
      this.gallery.render();
      this.onGalleryLoad();
      this.isGalleryLoaded = true;
    },
    toggleFullscreen() {
      let elem = document.getElementById(this.customId);

      if (!document.fullscreenElement) {
        elem.requestFullscreen().catch((err) => {
          alert(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
        });
      } else {
        document.exitFullscreen();
      }
    },
    trackFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    onImageLoad() {
      /**
       * Emitted when image is loaded.
       */
      this.$emit('onLoad');
    },
    onGalleryLoad() {
      /**
       * Emitted when gallery is loaded.
       */
      this.$emit('gallery-loaded');
    },
  },
};
</script>

<style lang="scss">
@import 'Image.scss';
</style>
